/**
 * Testimonials API - Carga reseñas desde un endpoint externo
 * Genera la misma estructura HTML que los bloques de testimonials.liquid
 */
class TestimonialsAPI {
  constructor(container) {
    this.container = container;
    this.wrapper = container.querySelector('.testimonials__wrapper');

    if (!this.wrapper) return;

    // Obtener configuración desde data attributes del contenedor
    this.apiUrl = container.dataset.apiUrl;
    this.perPage = parseInt(container.dataset.perPage) || 10;
    this.productId = container.dataset.productId;
    this.productHandle = container.dataset.productHandle;
    this.filterByProduct = container.dataset.filterByProduct === 'true';
    this.slideColorScheme = container.dataset.slideColorScheme || 'color-background-2';
    this.textSize = container.dataset.textSize || 'h6';
    this.contentAlign = container.dataset.contentAlign || 'left';
    this.layout = container.dataset.layout || 'simple_slider';
    this.cdnBase = 'https://babybelegal.com/cdn/shop/files/';

    this.init();
  }

  async init() {
    try {
      const reviews = await this.fetchReviews();
      if (reviews && reviews.length > 0) {
        this.renderReviews(reviews);
        // Reinicializar Swiper después de actualizar el contenido
        this.reinitSwiper();
      }
    } catch (error) {
      console.error('Error loading testimonials from API:', error);
      // En caso de error, mantener los bloques originales
    }
  }

  async fetchReviews() {
    let url = this.apiUrl;

    // Construir URL según configuración
    if (this.filterByProduct && this.productId) {
      url = `${this.apiUrl}/product/external/${this.productId}`;
    }


    // Agregar query params
    const params = new URLSearchParams({
      per_page: this.perPage,
      page: 1
    });

    url = `${url}?${params.toString()}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error('API returned unsuccessful response');
    }

    return data.reviews || [];
  }

  renderReviews(reviews) {
    const slidesHTML = reviews.map(review => this.createSlideHTML(review)).join('');
    this.wrapper.innerHTML = slidesHTML;
  }

  buildImageUrl(imageHandle, width = 140) {
    if (!imageHandle) return '';
    // Si ya es una URL completa, devolverla tal cual
    if (imageHandle.startsWith('http://') || imageHandle.startsWith('https://')) {
      return imageHandle;
    }
    // Construir URL del CDN de Shopify
    return `${this.cdnBase}${imageHandle}?width=${width}`;
  }

  createSlideHTML(review) {
    const rating = Math.min(5, Math.max(1, parseInt(review.rating) || 5));
    const ratingFormatted = rating + '.00';
    const reviewText = this.escapeHTML(review.body || '');
    const reviewerName = this.escapeHTML(review.reviewer?.name || '');
    const authorPosition = this.escapeHTML(review.author_position || '');
    const authorPicture = review.author_picture || '';

    // Construir URL de imagen
    const imageUrl = this.buildImageUrl(authorPicture);

    // Determinar si mostrar imagen grande (layouts image_left_slider o image_top_slider)
    const hasLargeImage = this.layout === 'image_left_slider' || this.layout === 'image_top_slider';

    // Generar HTML de imagen grande si aplica
    let authorImageHTML = '';
    if (hasLargeImage && imageUrl) {
      authorImageHTML = `
                        <div class="testimonials__author-image">
                          <img src="${this.buildImageUrl(authorPicture, 1200)}" alt="${reviewerName}" loading="lazy" class="motion-reduce media--second">
                        </div>`;
    }

    // Generar HTML del icono de autor (para layouts que no son image_left/image_top)
    let authorIconHTML = '';
    if (!hasLargeImage && imageUrl) {
      authorIconHTML = `
                              <div class="testimonials__author-icon">
                                <img src="${imageUrl}" alt="${reviewerName}" loading="lazy" width="70" height="140">
                              </div>`;
    }

    // Generar HTML de info del autor
    let authorInfoHTML = '';
    if (reviewerName || authorPosition) {
      const nameClass = (this.layout === 'full_slider' || this.layout === 'image_left_slider') ? 'testimonials__author-name body-large' : 'testimonials__author-name';
      const positionClass = (this.layout === 'simple_slider' || this.layout === 'image_top_slider') ? 'testimonials__author-position body-small' : 'testimonials__author-position';

      authorInfoHTML = `
                              <div class="testimonials__author-info">
                                ${reviewerName ? `<div class="${nameClass}">${reviewerName}</div>` : ''}
                                ${authorPosition ? `<div class="${positionClass}">${authorPosition}</div>` : ''}
                              </div>`;
    }

    // Generar HTML completo del autor
    let authorHTML = '';
    if (imageUrl || reviewerName || authorPosition) {
      authorHTML = `
                          <div class="testimonials__author">${authorIconHTML}${authorInfoHTML}
                          </div>`;
    }

    // Generar clase de text size
    const textClass = `testimonials__text ${this.textSize}`;

    // Estructura exacta del bloque en testimonials.liquid
    return `
                <div class="swiper-slide">
                  <div class="testimonials__slide testimonials__slide--${this.contentAlign} ${this.slideColorScheme}">${authorImageHTML}
                    <div class="testimonials__item">
                      <div class="testimonials__rating">
                        <div class="testimonials__stars count-stars-${rating}">
                          <i class="icon-star"></i>
                        </div>
                        <div class="testimonials__rating-count body-small">
                          ${ratingFormatted}
                        </div>
                      </div>
                      ${reviewText ? `
                      <p class="${textClass}">${reviewText}</p>` : ''}
${authorHTML}
                    </div>
                  </div>
                </div>`;
  }

  reinitSwiper() {
    // Buscar el slider dentro del contenedor
    const slider = this.container.querySelector('.testimonials__slider');
    if (!slider) return;

    // Si ya existe una instancia de Swiper, destruirla
    if (slider.swiper) {
      slider.swiper.destroy(true, true);
    }

    // Esperar un tick para que el DOM se actualice
    setTimeout(() => {
      // Reinicializar usando la misma lógica que testimonials.js
      if (typeof Swiper === 'undefined') {
        console.warn('Swiper not loaded');
        return;
      }

      const columns = Number(slider.dataset.columns) || 3;
      let columns750 = columns > 1 ? columns - 1 : columns;

      const navigation = this.container.querySelector('.testimonials__navigation');
      const pagination = this.container.querySelector('.testimonials__pagination');

      let swiperParams = {
        slidesPerView: 1,
        slidesPerGroup: 1,
        spaceBetween: 24,
        mousewheel: {
          forceToAxis: true,
        },
        breakpoints: {
          750: {
            slidesPerView: columns750,
            slidesPerGroup: columns750,
          },
          1200: {
            slidesPerView: columns,
            slidesPerGroup: columns,
          },
        },
      };

      if (slider.getAttribute('data-pagination') === 'true' && pagination) {
        swiperParams.pagination = {
          el: pagination,
          type: 'bullets',
          clickable: true,
        };
      }

      if (slider.getAttribute('data-navigation') === 'true' && navigation) {
        swiperParams.navigation = {
          nextEl: navigation.querySelector('.swiper-button-next'),
          prevEl: navigation.querySelector('.swiper-button-prev'),
        };
      }

      new Swiper(slider, swiperParams);
    }, 10);
  }

  escapeHTML(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

// Inicializar todas las secciones de testimonials con API configurada
document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('.testimonials[data-section-id][data-api-url]');

  containers.forEach(container => {
    if (container.dataset.apiUrl) {
      new TestimonialsAPI(container);
    }
  });
});

// Soporte para Section Rendering API (Theme Editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('.testimonials[data-section-id][data-api-url]');
    if (container && container.dataset.apiUrl) {
      new TestimonialsAPI(container);
    }
  });
}
